import { CollectionButton } from '@/components/collection-button'
import { Button } from '@/components/ui/button'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip'
import { BodyText, MutedText } from '@/components/ui/typography'
import { useAudio } from '@/hooks/useAudio'
import { useSettings } from '@/hooks/useSettings'
import { Link } from '@/i18n/routing'
import { useChineseFont } from '@/lib/chinese-font'
import { getIndexedDictionaryEntryPath } from '@/lib/dictionary-route-manifest'
import { cn } from '@/lib/utils'
import type { SearchResult } from '@/types/dictionary'
import {
  IconArrowRight,
  IconCopy,
  IconLoader2,
  IconSearch,
  IconVolume
} from '@tabler/icons-react'
import { useTranslations } from 'next-intl'

interface Props {
  selectedText: string
  results: SearchResult[] | undefined
  isLoading: boolean
  onSearch?: (text: string) => void
  onClose?: () => void
  onClearSelection?: () => void
  children: React.ReactNode
  copyTooltipOpen?: boolean
  onTooltipOpenChange?: (open: boolean) => void
  contextPinyin?: string
}

export function CharacterPreview({
  selectedText,
  results,
  isLoading,
  onSearch,
  onClose,
  onClearSelection,
  children,
  copyTooltipOpen,
  onTooltipOpenChange,
  contextPinyin
}: Props) {
  const chineseFont = useChineseFont()
  const open = !!selectedText
  const { settings } = useSettings()

  // Determine script type for audio
  const scriptType: 'simplified' | 'traditional' =
    settings.scriptDisplay === 'traditional' ? 'traditional' : 'simplified'
  const { play, isPlaying } = useAudio(scriptType)

  // Match by pinyin if context provided, otherwise use first result
  const firstResult =
    results && results.length > 0
      ? contextPinyin
        ? results.find((r) => {
            const normalized = (s: string) =>
              s.toLowerCase().replace(/[1-5\s]/g, '')
            return normalized(r.pinyin) === normalized(contextPinyin)
          }) || results[0]
        : results[0]
      : undefined

  // Merge definitions from all entries with matching character AND pinyin
  // Definitions now come directly from entry (from entry_translations)
  const matchingDefinitions =
    firstResult && results
      ? results
          .filter(
            (r) =>
              r.simplified === firstResult.simplified &&
              r.pinyin === firstResult.pinyin
          )
          .flatMap((r) => r.definitions || [])
      : []

  // Determine which text to use for audio (based on script preference)
  const textForAudio = firstResult
    ? scriptType === 'traditional' && firstResult.traditional
      ? firstResult.traditional
      : firstResult.simplified
    : selectedText

  const handlePlay = () => {
    if (textForAudio) {
      play(textForAudio)
    }
  }

  const closeAndClear = () => {
    onClose?.()
    onClearSelection?.()
  }

  const handleOpenChange = (newOpen: boolean) => {
    // Only close if we're explicitly closing (not due to a race condition)
    // The Popover's open state is controlled by selectedText, so if selectedText
    // is set, the popover should stay open even if onOpenChange is called
    if (!newOpen && !selectedText) {
      closeAndClear()
    }
    // If newOpen is true but selectedText is empty, don't do anything
    // This can happen during state transitions
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(selectedText)
      onTooltipOpenChange?.(true)
      setTimeout(() => onTooltipOpenChange?.(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const handleSearch = () => {
    if (onSearch) {
      onSearch(selectedText)
      closeAndClear()
    }
  }

  if (!selectedText) return <>{children}</>

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={false}>
      <PopoverAnchor asChild>
        <span
          style={{ display: 'inline-block', pointerEvents: 'auto' }}
          onClick={(e) => {
            // Prevent clicks on the anchor from closing the popover
            if (open) {
              e.stopPropagation()
            }
          }}
        >
          {children}
        </span>
      </PopoverAnchor>
      <PopoverContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="bg-card max-h-[65vh] w-80 space-y-3 overflow-y-auto rounded-2xl border p-4 shadow-lg"
        align="center"
        side="bottom"
        sideOffset={8}
        onInteractOutside={(e) => {
          // Prevent closing when clicking on selectable text
          const target = e.target as HTMLElement
          if (target.closest('[data-char-index]')) {
            e.preventDefault()
          }
        }}
        onPointerDownOutside={(e) => {
          // Prevent closing when clicking on selectable text
          const target = e.target as HTMLElement
          if (target.closest('[data-char-index]')) {
            e.preventDefault()
          } else {
            // Clicking outside (not on selectable text) - clear selection
            closeAndClear()
          }
        }}
        onEscapeKeyDown={() => {
          // Allow escape to close
          closeAndClear()
        }}
      >
        {isLoading ? (
          <div className="flex items-center justify-center p-6">
            <IconLoader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : results && results.length > 0 ? (
          firstResult ? (
            <>
              {/* Header row: Character + Audio + Actions */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex gap-2">
                  {/* Character and pinyin header */}
                  <div>
                    <h3 className={cn(chineseFont, 'text-xl')}>
                      {scriptType === 'traditional' && firstResult.traditional
                        ? firstResult.traditional
                        : firstResult.simplified}
                      {firstResult.traditional &&
                        firstResult.traditional !== firstResult.simplified &&
                        scriptType === 'simplified' && (
                          <span className="text-muted-foreground/70 ml-2 text-base">
                            {firstResult.traditional}
                          </span>
                        )}
                      {scriptType === 'traditional' &&
                        firstResult.simplified !== firstResult.traditional &&
                        firstResult.traditional && (
                          <span className="text-muted-foreground/70 ml-2 text-base">
                            {firstResult.simplified}
                          </span>
                        )}
                    </h3>
                    <BodyText className="text-muted-foreground text-sm">
                      {firstResult.pinyin}
                    </BodyText>
                  </div>
                  {/* Audio button */}
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Play word audio"
                    onClick={(e) => {
                      e.stopPropagation()
                      handlePlay()
                    }}
                    disabled={isPlaying}
                    className="shrink-0"
                  >
                    <IconVolume className="h-4 w-4" />
                  </Button>
                </div>
                {/* Action buttons */}
                <ActionButtons
                  onCopy={handleCopy}
                  onSearch={onSearch ? handleSearch : undefined}
                  entryForLink={firstResult}
                  tooltipOpen={copyTooltipOpen}
                  entryId={firstResult.id}
                />
              </div>

              {/* Definitions */}
              {matchingDefinitions.length > 0 ? (
                <div className="space-y-1 text-sm">
                  {matchingDefinitions.map((def, idx) => (
                    <p key={idx} className="text-foreground">
                      {idx + 1}. {def}
                    </p>
                  ))}
                </div>
              ) : (
                <BodyText className="text-muted-foreground text-sm">
                  No definitions available
                </BodyText>
              )}
            </>
          ) : (
            <div className="space-y-3">
              <MutedText>No details available for selection</MutedText>
              <ActionButtons
                onCopy={handleCopy}
                onSearch={onSearch ? handleSearch : undefined}
                tooltipOpen={copyTooltipOpen}
              />
            </div>
          )
        ) : (
          <div className="space-y-3">
            <div className="text-center">
              <MutedText>
                No match found for &quot;{selectedText}&quot;
              </MutedText>
            </div>
            <ActionButtons
              onCopy={handleCopy}
              onSearch={onSearch ? handleSearch : undefined}
              tooltipOpen={copyTooltipOpen}
            />
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

interface ActionButtonsProps {
  onCopy: () => void
  onSearch?: () => void
  onViewDetails?: () => void
  tooltipOpen?: boolean
  entryId?: number
  entryForLink?: SearchResult
}

function ActionButtons({
  onCopy,
  onSearch,
  onViewDetails,
  tooltipOpen,
  entryId,
  entryForLink
}: ActionButtonsProps) {
  const t = useTranslations('entry')

  const dictUrl = entryForLink
    ? getIndexedDictionaryEntryPath(entryForLink)
    : null

  return (
    <div className="flex items-center gap-1">
      {entryId && (
        <CollectionButton entryId={entryId} size="sm" variant="ghost" />
      )}
      <TooltipProvider>
        <Tooltip open={tooltipOpen}>
          <TooltipTrigger asChild>
            <Button
              size="icon-sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation()
                onCopy()
              }}
              aria-label="Copy"
            >
              <IconCopy className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Copied!</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {onSearch && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon-sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation()
                  onSearch()
                }}
                aria-label="Search"
              >
                <IconSearch className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Search</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {dictUrl && (
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <Button
              variant="secondary"
              size="icon-sm"
              aria-label={t('viewDetails')}
              className="shrink-0"
              asChild
            >
              <Link href={dictUrl}>
                <IconArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('viewDetails')}</TooltipContent>
        </Tooltip>
      )}
      {onViewDetails && !dictUrl && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon-sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation()
                  onViewDetails()
                }}
                aria-label={t('viewDetails')}
              >
                <IconArrowRight className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t('viewDetails')}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  )
}
